/**
 * VocabularyPage – flashcard-style list of saved words.
 * Status cycles: new → learning → learned (tap chip to advance).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CaretLeft, Trash, BookmarkSimple } from '@phosphor-icons/react';
import { http, apiErrorMessage } from '@/lib/api';

const NEXT_STATUS = { new: 'learning', learning: 'learned', learned: 'new' };
const CHIP_CLASS = { new: 'lf-chip', learning: 'lf-chip lf-chip-accent', learned: 'lf-chip lf-chip-success' };

export default function VocabularyPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get('/vocabulary');
        setItems(data);
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
  }, []);

  const visible = filter === 'all' ? items : items.filter((x) => x.status === filter);
  const counts = {
    all: items.length,
    new: items.filter((x) => x.status === 'new').length,
    learning: items.filter((x) => x.status === 'learning').length,
    learned: items.filter((x) => x.status === 'learned').length,
  };

  async function cycleStatus(item) {
    const next = NEXT_STATUS[item.status] || 'new';
    try {
      const { data } = await http.patch(`/vocabulary/${item.id}`, { status: next });
      setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  async function removeItem(id) {
    try {
      await http.delete(`/vocabulary/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  return (
    <div className="lf-shell px-6 py-8" data-testid="vocabulary-page">
      <div className="flex items-center gap-2">
        <Link to="/home" className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]" data-testid="vocab-back">
          <CaretLeft size={18} />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Your words</div>
          <h1 className="text-2xl font-bold">Vocabulary</h1>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="vocab-filters">
        {['all', 'new', 'learning', 'learned'].map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${
              filter === k
                ? 'bg-[var(--lf-primary)] text-white'
                : 'bg-[var(--lf-surface)] border border-[var(--lf-border)] text-[var(--lf-text)]'
            }`}
            data-testid={`vocab-filter-${k}`}
          >
            {k[0].toUpperCase() + k.slice(1)} <span className="opacity-60">· {counts[k]}</span>
          </button>
        ))}
      </div>

      {error ? <div className="mt-4 text-sm text-[var(--lf-destructive)]">{error}</div> : null}

      {visible.length === 0 ? (
        <div className="mt-10 text-center lf-card p-6" data-testid="vocab-empty">
          <BookmarkSimple size={32} weight="duotone" className="mx-auto text-[var(--lf-primary)]" />
          <div className="mt-2 font-semibold">No words here yet</div>
          <div className="text-sm text-[var(--lf-text-muted)]">
            Finish a session and save the vocabulary the coach suggests.
          </div>
        </div>
      ) : (
        <div className="mt-5 space-y-2">
          {visible.map((v) => (
            <div key={v.id} className="lf-card flex items-start gap-3 p-4" data-testid={`vocab-item-${v.id}`}>
              <div className="flex-1">
                <div className="font-bold text-lg">{v.source_text}</div>
                <div className="text-sm text-[var(--lf-text-muted)]">{v.translated_text}</div>
                {v.example_text ? (
                  <div className="mt-1 text-xs italic text-[var(--lf-text-muted)]">“{v.example_text}”</div>
                ) : null}
              </div>
              <div className="flex flex-col items-end gap-2">
                <button
                  onClick={() => cycleStatus(v)}
                  className={CHIP_CLASS[v.status]}
                  data-testid={`vocab-status-${v.id}`}
                >
                  {v.status}
                </button>
                <button
                  onClick={() => removeItem(v.id)}
                  className="text-[var(--lf-text-muted)] hover:text-[var(--lf-destructive)]"
                  data-testid={`vocab-delete-${v.id}`}
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="h-10" />
    </div>
  );
}
